import {
  ChargeItem,
  ChargeItemDefinition,
  Invoice,
  ServiceRequest,
  Patient,
  Extension,
  Attachment,
} from '@medplum/fhirtypes';
import { MedplumClient, getReferenceString } from '@medplum/core';
import { ARKON } from '@consts/systems.consts';
import { PaymentSource, PaymentStatus } from '../lims-types';
import {
  DEFAULT_CURRENCY,
  DEFAULT_PAYMENT_SOURCE,
  PAYMENT_SOURCE_REGISTRY,
  resolvePaymentSource,
} from '../constants/payment.consts';

export const buildExamChargeItem = (
  serviceRequest: ServiceRequest,
  patient: Patient,
  amount: number,
  source: PaymentSource,
  testCode?: string,
  testDisplay?: string,
  discount: number = 0
): ChargeItem => {
  return {
    resourceType: 'ChargeItem',
    status: 'billable',
    code: {
      coding: [
        {
          system: ARKON.CS.CHARGE_ITEM.BASE,
          code: ARKON.CS.CHARGE_ITEM.CODES.EXAM_CHARGE,
          display: testDisplay || 'Exam Charge',
        },
        ...(testCode
          ? [
              {
                system: ARKON.CS.GENETIC_TESTS.BASE,
                code: testCode,
                display: testDisplay,
              },
            ]
          : []),
      ],
    },
    subject: { reference: getReferenceString(patient) },
    service: [{ reference: getReferenceString(serviceRequest) }],
    quantity: { value: 1 },
    priceOverride: {
      value: Math.max(0, amount - discount),
      currency: DEFAULT_CURRENCY,
    },
    extension: [
      {
        url: ARKON.SD.PAYMENT_SOURCE,
        valueCode: source,
      },
      ...(discount > 0
        ? [
            {
              url: ARKON.SD.DISCOUNT,
              valueDecimal: discount,
            },
          ]
        : []),
    ],
  };
};

export const buildDepositChargeItem = (
  mainChargeItem: ChargeItem,
  patient: Patient,
  amount: number,
  attachments?: Attachment[]
): ChargeItem => {
  return {
    resourceType: 'ChargeItem',
    status: 'billed',
    code: {
      coding: [
        {
          system: ARKON.CS.CHARGE_ITEM.BASE,
          code: ARKON.CS.CHARGE_ITEM.CODES.DEPOSIT,
          display: 'Deposit',
        },
      ],
    },
    subject: { reference: getReferenceString(patient) },
    partOf: [{ reference: getReferenceString(mainChargeItem) }],
    service: mainChargeItem.service, // Link to same ServiceRequest as main charge
    quantity: { value: 1 },
    priceOverride: {
      value: amount,
      currency: DEFAULT_CURRENCY,
    },
    ...(attachments?.length && {
      extension: attachments.map((att) => ({
        url: ARKON.SD.PAYMENT_RECEIPT,
        valueAttachment: att,
      })),
    }),
  };
};

export const getDepositReceipts = (chargeItem: ChargeItem): Attachment[] => {
  const exts = chargeItem.extension?.filter(
    (e) => e.url === ARKON.SD.PAYMENT_RECEIPT
  );
  return (
    (exts?.map((e) => e.valueAttachment).filter(Boolean) as Attachment[]) || []
  );
};

// ---------------------------------------------------------------------------
// Invoice helpers
// ---------------------------------------------------------------------------

export const buildInvoiceForOrder = (
  serviceRequest: ServiceRequest,
  patient: Patient,
  chargeItems: ChargeItem[],
  totalGross: number,
  totalNet: number,
  testCode?: string,
  testDisplay?: string
): Invoice => {
  const invoice: Invoice = {
    resourceType: 'Invoice',
    status: 'draft', // Both callers currently override this
    subject: { reference: getReferenceString(serviceRequest) },
    recipient: { reference: getReferenceString(patient) },
    date: new Date().toISOString(),
    lineItem: chargeItems.map((ci) => ({
      chargeItemReference: { reference: getReferenceString(ci) },
    })),
    totalGross: { value: totalGross, currency: DEFAULT_CURRENCY },
    totalNet: { value: totalNet, currency: DEFAULT_CURRENCY },
    ...(testDisplay
      ? {
          type: {
            coding: [{ code: testCode, display: testDisplay }],
            text: testDisplay,
          },
        }
      : {}),
  };

  return invoice;
};

// ---------------------------------------------------------------------------
// Extension getters / setters
// ---------------------------------------------------------------------------

export const getPaymentSource = (resource: {
  extension?: Extension[];
}): PaymentSource | undefined => {
  const ext = resource.extension?.find(
    (e) => e.url === ARKON.SD.PAYMENT_SOURCE
  );
  return resolvePaymentSource(ext?.valueCode);
};

export const getLinkedOrderRef = (invoice: Invoice): string | undefined => {
  if (invoice.subject?.reference?.startsWith('ServiceRequest/')) {
    return invoice.subject.reference;
  }
  return undefined;
};

export const getDiscount = (resource: { extension?: Extension[] }): number => {
  const ext = resource.extension?.find((e) => e.url === ARKON.SD.DISCOUNT);
  return ext?.valueDecimal || 0;
};

export const setPaymentSourceExtension = (
  invoice: Invoice,
  source: PaymentSource
): Invoice => {
  const extensions = (invoice.extension || []).filter(
    (e) => e.url !== ARKON.SD.PAYMENT_SOURCE
  );
  extensions.push({ url: ARKON.SD.PAYMENT_SOURCE, valueCode: source });
  return { ...invoice, extension: extensions };
};

export const getInvoiceStatus = (invoice: Invoice): PaymentStatus => {
  return (invoice.status as PaymentStatus) || 'draft';
};

// ---------------------------------------------------------------------------
// Amount helpers
// ---------------------------------------------------------------------------

export const getDepositTotal = (depositItems: ChargeItem[]): number => {
  return depositItems.reduce(
    (sum, item) => sum + (item.priceOverride?.value || 0),
    0
  );
};

export const getRemainingBalance = (
  totalNet: number,
  depositItems: ChargeItem[]
): number => {
  return Math.max(0, totalNet - getDepositTotal(depositItems));
};

export const isFullyPaid = (
  totalNet: number,
  depositItems: ChargeItem[]
): boolean => {
  return totalNet - getDepositTotal(depositItems) <= 0.01;
};

export const addPaymentNote = (invoice: Invoice, text: string): Invoice => {
  const note = {
    time: new Date().toISOString(),
    text,
  };
  return {
    ...invoice,
    note: [...(invoice.note || []), note],
  };
};

// ---------------------------------------------------------------------------
// FHIR search / fetch helpers
// ---------------------------------------------------------------------------

export const findInvoiceForOrder = async (
  medplum: MedplumClient,
  serviceRequestId: string | undefined,
  _patientId?: string
): Promise<Invoice | undefined> => {
  if (!serviceRequestId) {
    return undefined;
  }
  const invoice = await medplum.searchOne('Invoice', {
    subject: `ServiceRequest/${serviceRequestId}`,
  });
  return invoice;
};

export const findChargeItemsForOrder = async (
  medplum: MedplumClient,
  serviceRequestId: string
): Promise<{ mainItems: ChargeItem[]; depositItems: ChargeItem[] }> => {
  const chargeItems = await medplum.searchResources('ChargeItem', {
    service: `ServiceRequest/${serviceRequestId}`,
    _count: '200', // prevent silent truncation on orders with many amendments
  });

  const mainItems: ChargeItem[] = [];
  const depositItems: ChargeItem[] = [];

  for (const ci of chargeItems) {
    const isDeposit = ci.code?.coding?.some(
      (c) =>
        c.system === ARKON.CS.CHARGE_ITEM.BASE &&
        c.code === ARKON.CS.CHARGE_ITEM.CODES.DEPOSIT
    );
    if (isDeposit) {
      depositItems.push(ci);
    } else {
      mainItems.push(ci);
    }
  }

  return { mainItems, depositItems };
};

// ---------------------------------------------------------------------------
// ChargeItemDefinition helpers
// ---------------------------------------------------------------------------

export const fetchChargeItemDefinition = async (
  medplum: MedplumClient,
  serviceRequest: ServiceRequest
): Promise<ChargeItemDefinition | undefined> => {
  const targetSystem = ARKON.CS.GENETIC_TESTS.BASE;

  // Find the coding that matches our target system
  const targetCoding = serviceRequest.code?.coding?.find(
    (c) => c.system === targetSystem
  );
  const code = targetCoding?.code || serviceRequest.code?.coding?.[0]?.code;

  if (!code) {
    return undefined;
  }

  // Use URL search parameter as 'code' parameter is not supported
  const definitionUrl = `${ARKON.BASE}/fhir/ChargeItemDefinition/${code}`;

  const definitions = await medplum.searchResources('ChargeItemDefinition', {
    url: definitionUrl,
    _count: '1',
  });

  return definitions?.[0];
};

export interface PricingOptionSplit {
  source: PaymentSource;
  amount: number;
  discount?: number;
  currency: string;
}

export interface PricingOption {
  label: string;
  totalAmount: number;
  currency: string;
  splits: PricingOptionSplit[];
}

export const getPricesFromDefinition = (
  definition: ChargeItemDefinition,
  t?: (key: string, options?: Record<string, unknown>) => string
): PricingOption[] => {
  const groups = definition.propertyGroup || [];
  const options: PricingOption[] = [];

  groups.forEach((group) => {
    const components = group.priceComponent || [];
    if (components.length === 0) {
      return;
    }

    const splits: PricingOptionSplit[] = [];
    let totalAmount = 0;
    let currency = DEFAULT_CURRENCY;
    const labelParts: string[] = [];

    components.forEach((comp) => {
      const amount = comp.amount?.value || 0;
      const compCurrency = comp.amount?.currency || DEFAULT_CURRENCY;
      const billing = comp.code?.coding?.[0];
      const code = billing?.code || 'UNKNOWN';

      if (splits.length === 0 && labelParts.length === 0) {
        currency = compCurrency as any; // Take first currency
      }

      const source = resolvePaymentSource(code);

      if (amount >= 0) {
        const resolvedSource = source ?? DEFAULT_PAYMENT_SOURCE;
        splits.push({
          source: resolvedSource,
          amount,
          discount: 0,
          currency: compCurrency,
        });
        totalAmount += amount;
        // Resolve the i18n key from the registry to get the display name at render time
        const displayName = source
          ? t
            ? t(PAYMENT_SOURCE_REGISTRY[source].i18nKey, { ns: 'lims' })
            : PAYMENT_SOURCE_REGISTRY[source].i18nKey
          : billing?.display || code;
        labelParts.push(`${amount} ${compCurrency} (${displayName})`);
      } else {
        // Zero-amount: covered by this billing category — show in label only
        // Prefer the coding display name, then fall back to the raw code
        const displayName = billing?.display || code;
        labelParts.push(displayName);
      }
    });

    options.push({
      label: labelParts.join(' + '),
      totalAmount,
      currency,
      splits,
    });
  });

  return options;
};
